
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from "@/components/ui/date-picker";
import { Clock, User, Calendar, AlertCircle, Timer, Filter, X } from 'lucide-react';
import { format, parseISO, isValid, startOfDay, endOfDay } from 'date-fns';
import { cn } from '@/lib/utils';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useSupabaseUser } from '@/hooks/use-supabase-user';

interface EarlyClockOut {
  id: string;
  user_id: string;
  shift_id: string;
  team_id?: string;
  scheduled_end_time: string;
  actual_clock_out_time: string;
  reason: string;
  minutes_early: number;
  created_at: string;
  profiles?: {
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
}

export const EarlyClockOutsList = () => {
  const { isAdmin, isSuperAdmin } = useUserRoles();
  const { user } = useSupabaseUser();
  const [dateFilter, setDateFilter] = useState<Date | undefined>(undefined);
  const [teamFilter, setTeamFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const { data: earlyClockOuts, isLoading, error: queryError } = useQuery({
    queryKey: ['early-clock-outs', user?.id, isAdmin, isSuperAdmin],
    queryFn: async () => {
      console.log('🔍 EarlyClockOuts - Starting query', { userId: user?.id, isAdmin, isSuperAdmin });
      
      if (!user?.id) {
        console.log('❌ No user ID');
        return [];
      }

      // Only admins and super admins can access this data
      if (!isAdmin && !isSuperAdmin) {
        console.log('❌ User is not admin or super admin');
        return [];
      }

      // For super admins, skip filtering and get all records
      if (isSuperAdmin) {
        console.log('👑 Super admin - fetching all records');
        const { data: clockOutsData, error: clockOutsError } = await supabase
          .from('early_clock_outs')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50);

        if (clockOutsError) {
          console.error('❌ Query error:', clockOutsError);
          throw clockOutsError;
        }

        if (!clockOutsData || clockOutsData.length === 0) {
          console.log('✅ No early clock outs found');
          return [];
        }

        console.log('📋 Found', clockOutsData.length, 'early clock outs, fetching profiles...');

        // Fetch profiles separately
        const userIds = [...new Set(clockOutsData.map(eco => eco.user_id))];
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name')
          .in('id', userIds);

        // Combine the data manually
        const result = clockOutsData.map(eco => ({
          ...eco,
          profiles: profilesData?.find(p => p.id === eco.user_id) || null
        }));

        console.log('✅ Super admin results:', result.length, 'records');
        return result as EarlyClockOut[];
      }

      // For regular admins, get guards from their admin group
      console.log('🔍 Regular admin - checking admin group');
      const { data: adminGroupMembers } = await supabase
        .from('admin_team')
        .select('member_id')
        .eq('admin_id', user.id)
        .in('member_type', ['parent_admin', 'contributor']);
      
      console.log('📋 Admin group members:', adminGroupMembers?.length || 0);
      
      if (!adminGroupMembers || adminGroupMembers.length === 0) {
        console.log('⚠️ No admin group - trying fallback (all records)');
        // Fallback: get all records without filtering
        const { data: clockOutsData, error: clockOutsError } = await supabase
          .from('early_clock_outs')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50);

        if (clockOutsError) {
          console.error('❌ Fallback query error:', clockOutsError);
          throw clockOutsError;
        }

        if (!clockOutsData || clockOutsData.length === 0) {
          console.log('✅ No early clock outs found');
          return [];
        }

        console.log('📋 Found', clockOutsData.length, 'early clock outs, fetching profiles...');

        // Fetch profiles separately
        const userIds = [...new Set(clockOutsData.map(eco => eco.user_id))];
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name')
          .in('id', userIds);

        // Combine the data manually
        const result = clockOutsData.map(eco => ({
          ...eco,
          profiles: profilesData?.find(p => p.id === eco.user_id) || null
        }));

        console.log('✅ Fallback results:', result.length, 'records');
        return result as EarlyClockOut[];
      }

      const adminIds = adminGroupMembers.map(m => m.member_id);
      console.log('👥 Admin IDs in group:', adminIds.length);
      
      // Now get all guards created by these admins
      const { data: guards } = await supabase
        .from('profiles')
        .select('id')
        .in('created_by', adminIds);
      
      console.log('👮 Guards found:', guards?.length || 0);
      
      if (!guards || guards.length === 0) {
        console.log('❌ No guards found for admin group');
        return [];
      }

      const guardIds = guards.map(g => g.id);
      console.log('🎯 Querying early_clock_outs for', guardIds.length, 'guards');
      
      // Query without the join first
      const { data: clockOutsData, error: clockOutsError } = await supabase
        .from('early_clock_outs')
        .select('*')
        .in('user_id', guardIds)
        .order('created_at', { ascending: false })
        .limit(50);

      if (clockOutsError) {
        console.error('❌ Clock outs query error:', clockOutsError);
        throw clockOutsError;
      }

      if (!clockOutsData || clockOutsData.length === 0) {
        console.log('✅ No early clock outs found');
        return [];
      }

      console.log('📋 Found', clockOutsData.length, 'early clock outs, fetching profiles...');

      // Fetch profiles separately
      const userIds = [...new Set(clockOutsData.map(eco => eco.user_id))];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name')
        .in('id', userIds);

      if (profilesError) {
        console.error('❌ Profiles query error:', profilesError);
        // Continue without profiles rather than failing
      }

      // Combine the data manually
      const result = clockOutsData.map(eco => ({
        ...eco,
        profiles: profilesData?.find(p => p.id === eco.user_id) || null
      }));

      console.log('✅ Final results:', result.length, 'records with profiles');
      return result as EarlyClockOut[];
    },
    enabled: !!user?.id && (isAdmin || isSuperAdmin)
  });

  // Log query error if any
  if (queryError) {
    console.error('❌ React Query Error:', queryError);
  }

  // Fetch teams for filter - MUST be before any conditional returns
  const { data: teams } = useQuery({
    queryKey: ['teams-for-filter', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('teams')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id
  });

  // Helper functions
  const formatDateTime = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      if (isValid(date)) {
        return format(date, 'MMM dd, yyyy HH:mm');
      }
    } catch {
      // Fallback for invalid dates
    }
    return dateString;
  };

  const getSeverityColor = (minutesEarly: number) => {
    if (minutesEarly >= 60) {
      return 'bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400 border border-red-500/20';
    } else if (minutesEarly >= 30) {
      return 'bg-orange-500/10 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400 border border-orange-500/20';
    } else {
      return 'bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400 border border-amber-500/20';
    }
  };

  // Filter early clock outs by date and team
  const filteredClockOuts = useMemo(() => {
    if (!earlyClockOuts) return [];

    return earlyClockOuts.filter(clockOut => {
      // Date filter
      if (dateFilter) {
        const clockOutDate = startOfDay(parseISO(clockOut.created_at));
        const filterDate = startOfDay(dateFilter);
        if (clockOutDate.getTime() !== filterDate.getTime()) {
          return false;
        }
      }

      // Team filter
      if (teamFilter && teamFilter !== 'all') {
        if (clockOut.team_id !== teamFilter) {
          return false;
        }
      }

      return true;
    });
  }, [earlyClockOuts, dateFilter, teamFilter]);

  const clearFilters = () => {
    setDateFilter(undefined);
    setTeamFilter('all');
  };

  const hasActiveFilters = dateFilter || teamFilter !== 'all';

  // Conditional returns AFTER all hooks
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-lg font-medium text-muted-foreground">Loading early clock outs...</p>
        </div>
      </div>
    );
  }

  if (!earlyClockOuts?.length) {
    return (
      <Card className="bg-gradient-to-br from-muted/30 to-muted/50 border-primary/20">
        <CardContent className="text-center py-16">
          <div className="flex flex-col items-center space-y-6">
            <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl">
              <Clock className="h-16 w-16 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-muted-foreground">No Early Clock Outs</h3>
              <p className="text-muted-foreground/70">No early clock out records have been found.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Section */}
      <Card className="border-primary/20">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Filters</h3>
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2">
                    {[dateFilter && 'Date', teamFilter !== 'all' && 'Team'].filter(Boolean).join(', ')}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-8"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="h-8"
                >
                  {showFilters ? 'Hide' : 'Show'} Filters
                </Button>
              </div>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="date-filter">Date</Label>
                  <DatePicker
                    date={dateFilter}
                    onSelect={setDateFilter}
                    placeholder="Pick a date"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="team-filter">Team</Label>
                  <Select value={teamFilter} onValueChange={setTeamFilter}>
                    <SelectTrigger id="team-filter">
                      <SelectValue placeholder="All Teams" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Teams</SelectItem>
                      {teams?.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <strong>{filteredClockOuts.length}</strong> of <strong>{earlyClockOuts.length}</strong> early clock outs
        </p>
      </div>

      {/* Results Grid */}
      {filteredClockOuts.length === 0 ? (
        <Card className="bg-gradient-to-br from-muted/30 to-muted/50 border-primary/20">
          <CardContent className="text-center py-12">
            <div className="flex flex-col items-center space-y-4">
              <Filter className="h-12 w-12 text-muted-foreground/50" />
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-muted-foreground">No Results</h3>
                <p className="text-sm text-muted-foreground/70">No early clock outs match your filters.</p>
              </div>
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
          {filteredClockOuts.map((clockOut, index) => (
        <Card 
          key={clockOut.id} 
          className={cn(
            "group transform transition-all duration-500 hover:scale-[1.02] hover:shadow-luxury",
            "bg-gradient-to-br from-background/95 via-background/90 to-muted/50 border-primary/20",
            "hover:border-primary/40 backdrop-blur-sm"
          )}
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <div className="relative overflow-hidden">
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 group-hover:from-primary/10 group-hover:to-accent/10 transition-all duration-300"></div>
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
            
            <div className="relative p-6 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-xl transition-all duration-300 group-hover:scale-110",
                    clockOut.minutes_early >= 60 ? 'bg-red-500 text-white' :
                    clockOut.minutes_early >= 30 ? 'bg-orange-500 text-white' :
                    'bg-amber-500 text-white'
                  )}>
                    <AlertCircle className="h-4 w-4" />
                  </div>
                  
                  <Badge className={cn(
                    "font-medium",
                    getSeverityColor(clockOut.minutes_early)
                  )}>
                    {clockOut.minutes_early} min early
                  </Badge>
                </div>
                
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 px-3 py-1 rounded-full">
                  <Clock className="w-3 h-3" />
                  {formatDateTime(clockOut.created_at)}
                </div>
              </div>

              {/* User Info */}
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <User className="w-4 h-4 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Guard</p>
                  <p className="text-sm font-semibold">
                    {clockOut.profiles?.full_name || 
                     `${clockOut.profiles?.first_name || ''} ${clockOut.profiles?.last_name || ''}`.trim() || 
                     'Unknown Guard'}
                  </p>
                </div>
              </div>

              {/* Time Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Timer className="h-4 w-4 text-primary" />
                  Clock Out Details
                </h4>
                
                <div className="grid grid-cols-1 gap-3 p-4 bg-muted/20 rounded-xl">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Scheduled End</p>
                      <p className="font-semibold">
                        {formatDateTime(clockOut.scheduled_end_time)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Actual Clock Out</p>
                      <p className="font-semibold">
                        {formatDateTime(clockOut.actual_clock_out_time)}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">Shift ID</p>
                    <p className="font-semibold text-xs font-mono">{clockOut.shift_id}</p>
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <h4 className="font-semibold text-foreground">Reason</h4>
                <div className="p-3 bg-muted/20 border border-border rounded-lg">
                  <p className="text-sm text-foreground">{clockOut.reason}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
          ))}
        </div>
      )}
    </div>
  );
};
